'use client'

import { MenuItem, Select, IconButton, Button, CircularProgress } from '@achieve/ascend'
import { Typography } from 'components/Contentful'
import { Eyebrow } from 'components/Eyebrow'
import { Image } from 'components/Image'
import { useEffect, useMemo, useRef, useState } from 'react'
import { getUGCStyles, getUGCTemplates } from './data'
import classNames from 'classnames'
import { ChevronLeft, ChevronRight } from 'react-feather'
import { DotIndicators } from 'components/DotIndicators'
import logger from '@achieve/cx-logger'
import { WALL_OF_FAME_ROUTE } from 'constants'
import { useSearchParams } from 'next/navigation'
import { useTriggerEvents } from 'hooks/useTriggerEvents'
import { handleTrackAndReactEvent } from 'utils/analytics'

const shuffleArray = (array) => {
  const shuffled = [...array]
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
  }
  return shuffled
}

const BreakUpLetter = ({ story, letter }) => {
  return (
    <div>
      <div className="relative bg-blue-50 py-5 px-6 flex flex-col gap-2 lg:gap-4 w-full">
        <Image
          src="/multi-site-assets/wall-of-fame/wof-corner-4.svg"
          alt="wall of fame corner 1"
          width={187}
          height={136}
          className="absolute bottom-0 right-0"
        />
        <Typography
          className={`font-bold text-left md:text-center md:text-[32px] text-2xl p-0! text-wrap max-w-[380px] mx-auto`}
          content={'Your breakup letter is ready!'}
        />
        <Typography
          className={`text-left lg:text-center md:text-lg text-base p-0! text-wrap lg:max-w-[430px] max-w-[200px] w-full mx-0 lg:mx-auto`}
          content={'Read your letter, then explore more debt breakups from the community.'}
        />
      </div>
      <div className="overflow-hidden relative mt-6 bg-white pb-4 pt-10 border border-solid border-neutral-150 rounded-md max-w-[432px] lg:mx-auto mx-6 p-6">
        <Image
          src="/multi-site-assets/wall-of-fame/wof-confetti.svg"
          alt="wall of fame confetti"
          width={433}
          height={68}
          className="absolute -top-4 right-0"
        />
        <div className="bg-white rounded-md flex flex-col gap-4 w-full text-[#010F66]">
          <Typography
            className={`uppercase text-left text-sm text-wrap tracking-[4px]`}
            fontWeight="bold"
            content={'Dear debt,'}
          />
          <Typography
            className={`text-left text-sm lg:text-base text-wrap`}
            fontWeight="regular"
            content={letter}
          />
          <div>
            <Typography
              className={`text-left text-sm lg:text-base text-wrap`}
              fontWeight="regular"
              content={`No longer yours,`}
            />
            <Typography
              className={`text-left text-sm lg:text-base text-wrap`}
              fontWeight="regular"
              content={story.name}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

const BoxOption = ({ text, selected, onClick, page, className }) => {
  return (
    <div
      onClick={() => onClick?.({ text, page })}
      className={classNames(
        'cursor-pointer py-3  rounded-md bg-white overflow-hidden flex justify-center items-center border border-solid',
        selected ? 'border-[#154199]' : 'border-neutral-150',
        className
      )}
    >
      <Typography content={text} />
    </div>
  )
}

const generateBreakUpStory = ({ option1, option2, option3, option4, option5, story }) => {
  const totalDebtAmount = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  }).format(story.totalDebtAmount)
  return `“It's over because ${option1}. When I started this journey, I felt ${option2} and after ${story?.daysToFirstResolution} days, I said, ${option3} to my 1st debt! In just ${story?.totalMonths} months, I traded ${story?.totalResolvedDebts} debts for ${option4}. Now that ${totalDebtAmount} is behind me, I can ${option5}.”`
}

export const CardPremiumStory = ({ onClose, ...story }) => {
  const UGC = useMemo(() => getUGCTemplates(story), [story])
  const UGC_styles = useMemo(() => getUGCStyles(), [])
  const [option1] = useState(() =>
    shuffleArray(UGC[0].options).map((item) => ({ value: item, label: item }))
  )
  const [option2] = useState(() =>
    shuffleArray(UGC[1].options).map((item) => ({ value: item, label: item }))
  )
  const [option3] = useState(() =>
    shuffleArray(UGC[2].options).map((item) => ({ value: item, label: item }))
  )
  const [option4] = useState(() =>
    shuffleArray(UGC[3].options).map((item) => ({ value: item, label: item }))
  )
  const [option5] = useState(() =>
    shuffleArray(UGC[4].options).map((item) => ({ value: item, label: item }))
  )
  const [optionValue1, setOptionValue1] = useState(option1[0].value)
  const [optionValue2, setOptionValue2] = useState(option2[0].value)
  const [optionValue3, setOptionValue3] = useState(option3[0].value)
  const [optionValue4, setOptionValue4] = useState(option4[0].value)
  const [optionValue5, setOptionValue5] = useState(option5[0].value)
  const searchParams = useSearchParams()
  const { waitForNavigation, fireAndForget } = useTriggerEvents()
  const hasUgcStarted = useRef(false)
  const [page, setPage] = useState(0)
  const [loading, setLoading] = useState(false)
  const totalPages = UGC.length + 1
  const breakUpStory = generateBreakUpStory({
    option1: optionValue1,
    option2: optionValue2,
    option3: optionValue3,
    option4: optionValue4,
    option5: optionValue5,
    story,
  })

  const UGCSelected = UGC[page]
  const UGCSelectedStyle = UGC_styles[page]

  const goToNext = () => {
    if (page < totalPages - 1) {
      setPage((prev) => prev + 1)
    }
  }

  const goToPrev = () => {
    if (page > 0) {
      setPage((prev) => prev - 1)
    }
  }

  const handleUnpublishClick = async () => {
    const auth = searchParams.get('auth')
    setLoading(true)
    try {
      const res = await fetch(`/api/wall-of-fame/unpublish-graduate?auth=${auth}`, {
        method: 'POST',
      })

      await waitForNavigation({
        event_type: 'track',
        ...handleTrackAndReactEvent(event, {
          event_type: 'track',
          application: 'fdr-site',
          event_action: 'click',
          event_name: 'Unpublish Graduate',
          event_datetime: Date.now().toString(),
          event_id: 'unpublish_graduate',
        }),
      }).then(() => {
        setPage(story.length - 1)
        window.location.href = WALL_OF_FAME_ROUTE
      })

      if (!res.ok) {
        throw new Error('Failed to unpublish')
      }
    } catch (err) {
      logger.error('Failed to unpublish in WallOfFameStoryModalCarousel', err)
    } finally {
      setLoading(false)
    }
  }

  const handlePublishLetter = async () => {
    const auth = searchParams.get('auth')
    setLoading(true)
    try {
      const res = await fetch(`/api/wall-of-fame/update-graduate-letter?auth=${auth}`, {
        method: 'POST',
        body: JSON.stringify({ letter: breakUpStory }),
      })

      await waitForNavigation({
        event_type: 'track',
        ...handleTrackAndReactEvent(event, {
          event_type: 'track',
          application: 'fdr-site',
          event_action: 'click',
          event_name: 'UGC Completed',
          event_datetime: Date.now().toString(),
          event_id: 'ugc_completed',
        }),
      }).then(() => {
        window.location.href = WALL_OF_FAME_ROUTE
      })

      if (!res.ok) {
        throw new Error('Failed to publish letter')
      }
    } catch (err) {
      logger.error('Failed to publish Letter', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (page === 1 && !hasUgcStarted.current) {
      hasUgcStarted.current = true
      fireAndForget({
        event_type: 'track',
        ...handleTrackAndReactEvent(event, {
          event_type: 'track',
          application: 'fdr-site',
          event_name: 'WoF UGC Started',
          event_datetime: Date.now().toString(),
          event_id: 'wof_ugc_started',
        }),
      })
    }
  }, [fireAndForget, page])

  return (
    <div className={`md:w-[672px] w-[327px] relative rounded-md bg-white overflow-hidden`}>
      <div onClick={onClose} className="absolute cursor-pointer top-6 right-4 z-20">
        <Image
          className="invert"
          src="/multi-site-assets/close.svg"
          alt="Close button"
          width={19}
          height={18}
          preload={false}
          aria-hidden={true}
          data-testid="card-story-x-icon"
        />
      </div>

      {page >= 0 && page < UGC.length && (
        <div className={`relative flex flex-col items-center`}>
          <div
            className={`flex flex-col items-center z-10 ${UGCSelectedStyle.textColor} w-full ${UGCSelectedStyle.bgColor} px-6 py-8`}
          >
            <Image
              alt="Illustration of a confetti"
              height={104}
              width={104}
              className="md:w-[104px] md:h-[104px] w-[80px] h-[80px] self-center mb-8 p-0!"
              preload={false}
              src={UGCSelected.icon}
            />
            {UGCSelected?.eyebrow && (
              <Eyebrow
                className={`p-0! text-sm mb-4 ${UGCSelectedStyle.textColor}`}
                content={UGCSelected.eyebrow}
              />
            )}
            {UGCSelected?.title && (
              <Typography
                className={`text-center md:text-lg text-base p-0! ${UGCSelectedStyle.textColor} text-wrap`}
                content={UGCSelected.title}
              />
            )}
          </div>
          <div className="w-full flex flex-col items-center gap-4 text-[#010F66] px-6 pt-8 pb-0 max-w-[450px] mx-auto">
            <Typography
              className={`text-center md:text-[32px] text-2xl font-bold p-0! text-wrap`}
              content={UGCSelected?.subtitle}
            />
            {page === 0 && (
              <Select
                className="bg-white rounded-md w-full"
                displayEmpty={true}
                variant="standard"
                value={optionValue1}
                onChange={(e) => setOptionValue1(e.target.value)}
                fullWidth={true}
                data-testid="data-select-field-1"
              >
                {option1.map(({ label, value }) => (
                  <MenuItem key={label} value={value}>
                    <Typography content={label} />
                  </MenuItem>
                ))}
              </Select>
            )}
            {page === 1 && (
              <div className="grid grid-cols-2 gap-4 w-full">
                {option2.map(({ label, value }, idx) => (
                  <BoxOption
                    key={label}
                    page={idx}
                    text={label}
                    selected={optionValue2 === value}
                    onClick={() => setOptionValue2(value)}
                  />
                ))}
              </div>
            )}

            {page === 2 && (
              <Select
                className="bg-white rounded-md w-full"
                displayEmpty={true}
                variant="standard"
                value={optionValue3}
                onChange={(e) => setOptionValue3(e.target.value)}
                fullWidth={true}
                data-testid="data-select-field-3"
              >
                {option3.map(({ label, value }) => (
                  <MenuItem key={label} value={value} instanceId={value}>
                    <Typography content={label} />
                  </MenuItem>
                ))}
              </Select>
            )}
            {page === 3 && (
              <Select
                className="bg-white rounded-md w-full"
                displayEmpty={true}
                variant="standard"
                value={optionValue4}
                onChange={(e) => setOptionValue4(e.target.value)}
                fullWidth={true}
                data-testid="data-select-field-4"
              >
                {option4.map(({ label, value }) => (
                  <MenuItem key={label} value={value} instanceId={value}>
                    <Typography content={label} />
                  </MenuItem>
                ))}
              </Select>
            )}
            {page === 4 && (
              <Select
                className="bg-white rounded-md w-full"
                displayEmpty={true}
                variant="standard"
                value={optionValue5}
                onChange={(e) => setOptionValue5(e.target.value)}
                fullWidth={true}
                data-testid="data-select-field-5"
              >
                {option5.map(({ label, value }) => (
                  <MenuItem key={label} value={value} instanceId={value}>
                    <Typography content={label} />
                  </MenuItem>
                ))}
              </Select>
            )}
          </div>
        </div>
      )}

      {/* Letter */}
      {page === UGC.length && <BreakUpLetter story={story} letter={breakUpStory} />}

      {/* Pagination */}
      {page >= 0 && page <= UGC.length && (
        <div className="bg-white flex justify-center items-center">
          <div className="flex self-center p-4 gap-3">
            <IconButton
              className={`transition ${
                page === 0 ? 'cursor-not-allowed text-gray-400' : 'hover:bg-neutral-75'
              }`}
              onClick={goToPrev}
              aria-label="Previous story"
              disabled={page === 0}
            >
              <ChevronLeft size={24} />
            </IconButton>
            <DotIndicators
              variant="onWhite"
              size={totalPages}
              activepage={page}
              classNameIndicators="flex gap-4"
              classNameIndicatorDots="h-[6px] w-[6px] m-0 opacity-100 transition-all"
            />
            <IconButton
              onClick={goToNext}
              className={`transition ${
                page === totalPages - 1 ? 'cursor-not-allowed text-gray-400' : 'hover:bg-neutral-75'
              }`}
              aria-label="Next story"
              disabled={page === totalPages - 1}
            >
              <ChevronRight size={24} />
            </IconButton>
          </div>
        </div>
      )}

      {page === totalPages - 1 && (
        <div className="flex flex-col justify-center gap-2 w-full max-w-[432px] mx-auto mb-4 px-6">
          {loading ? (
            <div className="flex items-center justify-center">
              <CircularProgress size={40} />
            </div>
          ) : (
            <>
              <Button
                className="bg-blue-600 text-white px-4 py-2"
                onClick={handlePublishLetter}
                disabled={loading}
              >
                Publish letter
              </Button>
              <Button
                variant="text"
                className="text-content-accent px-4 py-2"
                onClick={handleUnpublishClick}
                disabled={loading}
              >
                Unpublish story
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  )
}
